// @ts-nocheck

import * as R from 'ramda';
import {
  BLOCKED_CUSTOM_EVENT_PREFIX,
  CUSTOM_EVENT_PREFIX,
  generateTouchToEventTransformers,
  getRowValuesFromColumnHelpers,
  populateRowsFromGroupBy,
} from '../../../common/support/utils';
import type { GroupByResponse, GroupByResult, Query } from '../../types/druidTypes';
import type {
  AllFraudRuleTypes,
  CodeToDescriptionMapping,
  CodeToNameMapping,
  FraudAgentsIndexed,
  FraudFilter,
  FraudTablePageState,
  ThresholdRules,
} from '../../types/fraudTypes';
import type { Column, Rows } from '../../types/tableTypes';
import { DEFAULT_ACTIVE_HEADER, DIMENSIONS_REQUIRED_FOR_COLUMNS, FRAUD_DIMENSIONS } from './fraudConstants';
const UNPOPULATED_VALUE = 'Unpopulated';
export const getInitActiveHeader = (pageState: FraudTablePageState | null | undefined): string => {
  return (pageState && pageState.activeHeader) || DEFAULT_ACTIVE_HEADER;
};
// determines if extendWithFraudNames needs to be called
export const codeToNameConversionNecessary = (
  data: GroupByResponse,
  pageState: FraudTablePageState | null | undefined
): boolean => {
  const queryDimensions = R.pathOr([], ['query', 'dimensions'], pageState);
  return queryDimensions.includes(FRAUD_DIMENSIONS.FRAUD_NAMES) && !!data;
};
// [EG-2380] Convert fraud names to title case with spaces, leaving 'IP' as is
// i.e. DEVICE_CONFLICT => Device Conflict, CONVERSION_TIME => Conversion Time, IP => IP
export const formatFraudName = (name: string): string => {
  //Converts a single word to Title Case, with a special case for "IP".
  const toTitleCase = (word: string): string => {
    const trimmedWord = word.trim().toLowerCase();
    return trimmedWord === 'ip' ? 'IP' : trimmedWord.charAt(0).toUpperCase() + trimmedWord.slice(1);
  };

  // Processes comma-separated words, applying title case to each.
  const formatCommaSeparatedWords = (words: string): string => {
    return words.split(',').map(toTitleCase).join(', ');
  };

  // formatted (no longer includes underscores), leave the name as is
  if (name.includes(' ')) return name;

  // Split by underscore, process each section separately
  return name
    .split('_')
    .map(formatCommaSeparatedWords) // Handle potential comma-separated values in each section
    .join(' ');
};
// replace uppercase fraud name with properly formatted fraud name
// calls formatFraudName (above) on each occurrence of a fraud name and replaces it
export const formatFraudNames = (response: GroupByResponse) => {
  const formatHelper = (results: Array<GroupByResult>) => {
    return results.map((v) => {
      if (v.event && v.event.fraud_names) {
        const unformattedFraudName = v.event.fraud_names;
        const path = R.lensPath(['event', 'fraud_names']);
        return R.set(path, formatFraudName(unformattedFraudName), v);
      }

      return v;
    });
  };

  return R.mapObjIndexed(formatHelper, response);
};
// get the corresponding fraud name for a fraud code
export const getFraudNameFromFraudCode = (fraudCode: string, codeToNameMapping: CodeToNameMapping): AllFraudRuleTypes => {
  // EG-2457 Special cases for PERSONA_FRAUD and ONCE_EVER_CAPPED fraud codes
  if (fraudCode === 'PERSONA_FRAUD') {
    return 'PERSONA_FRAUD';
  }

  if (fraudCode === 'ONCE_EVER_CAPPED') {
    return 'ONCE_EVER_CAPPED';
  }

  return codeToNameMapping[fraudCode];
};
export const appendFraudNameWithFraudCode = (fraudName: AllFraudRuleTypes, fraudCode: string): string => {
  // EG-2381, EG-2642 Force Custom and Threshold fraud rules to appear separately in the table by postfixing each with its unique fraud code
  if (fraudName === 'CUSTOM' || fraudName === 'THRESHOLD') {
    return fraudName + fraudCode;
  }

  return fraudName;
};
// extend druid response JSON values that have fraud code values with
// corresponding fraud name values to be rendered in the fraud table
export const extendWithFraudNames = (response: GroupByResponse, codeToNameMapping: CodeToNameMapping) => {
  const getFraudNamesByCodes = (fraudCodes: string[], codeToNameMapping: CodeToNameMapping): string[] => {
    return fraudCodes.map((code) => {
      const fraudName = getFraudNameFromFraudCode(code, codeToNameMapping);
      return appendFraudNameWithFraudCode(fraudName, code);
    });
  };

  const extendHelper = (results: Array<GroupByResult>) => {
    return results.map((v) => {
      if (v.event && v.event.fraud_codes) {
        const fraudCodes: string[] = v.event.fraud_codes.split(',');
        const fraudNames = getFraudNamesByCodes(fraudCodes, codeToNameMapping);

        const path = R.lensPath(['event', 'fraud_names']);
        return R.set(path, fraudNames.join(','), v);
      }

      return v;
    });
  };

  return R.mapObjIndexed(extendHelper, response);
};
// filter table data based on the filter provided in the Druid Query
// this front-end filtering is necessary because a single event may be tagged with multiple fraud names and codes
// for example, a Druid response may include records with fraud name = GEO_CONFLICT and fraud name = IP even though only GEO_CONFLICT was specified in the filter
export const filterTableRows = (filter: FraudFilter, tableRows: Rows, compareByDimension: string): Rows => {
  // Extracts filter values for the specified dimension and applies formatting.
  const getFilterValues = (filter: FraudFilter, compareByDimension: string): string[] => {
    if (!filter.fields) return [];

    const field = filter.fields.find((filterRow) => filterRow.dimension === compareByDimension);

    return field ? field.values.map(formatFraudName) : [];
  };

  // Checks if a row matches any of the filter values for the given dimension.
  const doesRowMatchFilter = (rowValue: string, filterValues: string[], compareByDimension: string): boolean => {
    if (!rowValue) return false;
    // Special handling for fraud_names and fraud_codes (split by comma & trim spaces)
    const doMatchFilterForNamesAndCodes = () => {
      if (compareByDimension === FRAUD_DIMENSIONS.FRAUD_CODES) {
        const fraudNamesArray = rowValue.split(',').map((name) => name.trim().toLowerCase());
        return fraudNamesArray.some((name) =>
          filterValues.some((filterValue) => formatFraudName(name).startsWith(filterValue))
        );
      }

      if (compareByDimension === FRAUD_DIMENSIONS.FRAUD_NAMES) {
        const fraudNamesArray = rowValue.split(',').map((name) => name.trim());
        return fraudNamesArray.some((name) => filterValues.some((filterValue) => name.startsWith(filterValue)));
      }
    };

    if (compareByDimension === FRAUD_DIMENSIONS.FRAUD_NAMES || compareByDimension === FRAUD_DIMENSIONS.FRAUD_CODES) {
      return doMatchFilterForNamesAndCodes();
    }

    // Standard comparison for other dimensions
    return filterValues.map((v) => v.toLowerCase()).includes(formatFraudName(rowValue).toLowerCase());
  };

  const filterValues = getFilterValues(filter, compareByDimension);

  if (!filterValues.length) return tableRows; // No filtering needed if no matching filter

  return tableRows.filter((row) => doesRowMatchFilter(row[compareByDimension], filterValues, compareByDimension));
};
export const getFilteredRows = (dimensions: Array<string>, filter: FraudFilter, tableRows: Rows) => {
  if (dimensions.includes(FRAUD_DIMENSIONS.FRAUD_NAMES) && dimensions.includes(FRAUD_DIMENSIONS.FRAUD_CODES)) {
    // filter on both fraud name and fraud code
    const filteredByName = filterTableRows(filter, tableRows, FRAUD_DIMENSIONS.FRAUD_NAMES);
    return filterTableRows(filter, filteredByName, FRAUD_DIMENSIONS.FRAUD_CODES);
  } else if (dimensions.includes(FRAUD_DIMENSIONS.FRAUD_NAMES)) {
    return filterTableRows(filter, tableRows, FRAUD_DIMENSIONS.FRAUD_NAMES);
  } else if (dimensions.includes(FRAUD_DIMENSIONS.FRAUD_CODES)) {
    return filterTableRows(filter, tableRows, FRAUD_DIMENSIONS.FRAUD_CODES);
  } else {
    return tableRows;
  }
};
export const updateDimensionsBasedOnColumns = (columns: Array<any>) => {
  return columns.reduce((acc, column) => {
    const columnToDimensionInfo = DIMENSIONS_REQUIRED_FOR_COLUMNS[column.key];

    if (columnToDimensionInfo) {
      if (acc[columnToDimensionInfo.source] && acc[columnToDimensionInfo.source].dimensions) {
        acc[columnToDimensionInfo.source].dimensions = acc[columnToDimensionInfo.source].dimensions.concat(
          columnToDimensionInfo.dimension
        );
      } else {
        acc[columnToDimensionInfo.source] = {
          dimensions: [columnToDimensionInfo.dimension],
        };
      }
    }

    return acc;
  }, {});
};
// [EG-2656] convert custom event names from eo_custom_event- prefix to eo_custom_event_blocked- prefix
// since these event names are coming from the eo_custom_event_blocked datasource
export const getBlockedCustomEventList = (customEventList: string[]): string[] => {
  return customEventList.map((name) => BLOCKED_CUSTOM_EVENT_PREFIX + name.slice(CUSTOM_EVENT_PREFIX.length));
};
// [EG-3048] remove rows that have a null or undefined value for fraud_names or fraud_codes
export const removeNullishFraudRows = (tableRows: Rows, dimensions: string[]): Rows => {
  const includedFraudDimensions = dimensions.filter(
    (dim) => dim === FRAUD_DIMENSIONS.FRAUD_NAMES || dim === FRAUD_DIMENSIONS.FRAUD_CODES
  );
  return tableRows.filter((row) => {
    return includedFraudDimensions.every((dim) => {
      return row[dim] !== undefined && row[dim] !== null && row[dim] !== UNPOPULATED_VALUE;
    });
  });
};
export const formatData = (selectedColumns: Column[], metric: string, dimensions: string[], data: GroupByResponse) => {
  const formattedData = formatFraudNames(data);
  const eoInstallBuckets = {
    seconds_from_last_attributed_touch_to_event: [
      [0, 9],
      [10, 29],
      [30, 60],
    ],
    days_from_last_attributed_touch_to_event: [
      [0, 0],
      [1, 90],
    ],
  };
  const eoInstallBucketsToUse = selectedColumns
    .filter((column) => {
      return Object.keys(DIMENSIONS_REQUIRED_FOR_COLUMNS).includes(column.group && column.group.key);
    })
    .reduce((acc, column) => {
      const dimension = DIMENSIONS_REQUIRED_FOR_COLUMNS[column.group && column.group.key].dimension;
      acc[dimension] = eoInstallBuckets[dimension];
      return acc;
    }, {});
  const populatedData = populateRowsFromGroupBy(dimensions, formattedData, metric, {
    ...generateTouchToEventTransformers(['eo_install'], {
      eo_install: eoInstallBucketsToUse,
    }),
  });
  return getRowValuesFromColumnHelpers(selectedColumns, populatedData);
};
// [EG-2380] Use fraud_codes field in place of fraud_names to avoid array isues with druid
export const maskFraudNamesWithFraudCodes = (query: Query) => {
  if (query.dimensions && query.dimensions.includes(FRAUD_DIMENSIONS.FRAUD_NAMES)) {
    const fraudNamesIndex = query.dimensions.indexOf(FRAUD_DIMENSIONS.FRAUD_NAMES);

    // if fraud_codes is not already a selected dimension, replace fraud_names with fraud_codes
    if (query.dimensions.includes(FRAUD_DIMENSIONS.FRAUD_CODES)) {
      query.dimensions.splice(fraudNamesIndex, 1);
    } else {
      query.dimensions[fraudNamesIndex] = FRAUD_DIMENSIONS.FRAUD_CODES;
    }
  }

  return query;
};
// determines if a fraud_names or fraud_codes filter has been selected
export const fraudFilterSelected = (query: Query): boolean => {
  if (!R.path(['filter', 'fields'], query)) {
    return false;
  }

  let fraudFilterFound = false;
  query &&
    query.filter.fields.forEach((field) => {
      if (field.dimension === FRAUD_DIMENSIONS.FRAUD_NAMES || field.dimension === FRAUD_DIMENSIONS.FRAUD_CODES) {
        fraudFilterFound = true;
      }
    });
  return fraudFilterFound;
};
// removes fraud_names and fraud_codes filters
export const stripFraudFilters = (query: Query): Query => {
  if (!R.path(['filter', 'fields'], query)) {
    return query;
  }

  const newQuery = R.clone(query);
  const queryFilter = query.filter;
  const strippedFilter = queryFilter.fields.filter(
    (field) => field.dimension !== FRAUD_DIMENSIONS.FRAUD_NAMES && field.dimension !== FRAUD_DIMENSIONS.FRAUD_CODES
  );

  if (strippedFilter.length) {
    newQuery.filter.fields = strippedFilter;
  } else {
    newQuery.filter = {};
  }

  return newQuery;
};
// replace fraud_names with fraud_codes to avoid array issues with druid
// remove fraud_names and fraud_codes filters since unblocked datasources will never return data with these filters
// filters on these dimensions will be completed at the UI-level after Cenarius responds instead of at the outgoing query level
export const modifyQueryForFraud = (query: Query): Query => {
  let newQuery = R.clone(query);

  // [EG-2380] Use fraud_codes field in place of fraud_names to avoid array isues with druid
  if (query.dimensions && query.dimensions.includes(FRAUD_DIMENSIONS.FRAUD_NAMES)) {
    newQuery = maskFraudNamesWithFraudCodes(newQuery);
  }

  // [EG-3109] Remove fraud_names and fraud_codes filters since unblocked datasources will never return data with these filters
  if (fraudFilterSelected(newQuery)) {
    newQuery = stripFraudFilters(newQuery);
  }

  return newQuery;
};
// determine if a dimension is a fraud dimension and included in a user-selected filter
export const isFraudDimensionInFilter = (dimension: string, query?: Query): boolean => {
  if (dimension !== FRAUD_DIMENSIONS.FRAUD_NAMES && dimension !== FRAUD_DIMENSIONS.FRAUD_CODES) {
    return false;
  }

  if (!R.path(['filter', 'fields'], query)) {
    return false;
  }

  let dimensionInFilter = false;
  query &&
    query.filter.fields.forEach((field) => {
      if (field.dimension === dimension) {
        dimensionInFilter = true;
      }
    });
  return dimensionInFilter;
};
export const createThresholdMapping = (
  thresholdRules: ThresholdRules,
  fraudAgents: FraudAgentsIndexed
): CodeToDescriptionMapping => {
  const thresholdMapping = {};
  // lookup and store agent_description and agent_long_description for each rule that has an agent_id
  thresholdRules.forEach((rule) => {
    const { id, agent_id } = rule;

    if (agent_id && fraudAgents[agent_id]) {
      const { agent_description, agent_long_description } = fraudAgents[agent_id];
      const description = agent_description || '[agent description]'; // fallback case should never happen - only included for flow reasons

      const longDescription = agent_long_description || '[agent long description]'; // fallback case should never happen - only included for flow reasons

      thresholdMapping[id] = `${description}: ${longDescription}`;
    }
  });
  return thresholdMapping;
};
